/**
 * Phase 5.4: ResponsivePositionMerger
 *
 * This service handles deep merging of property changes into existing responsive positions,
 * preventing overwriting of unrelated breakpoint styles when updating responsivePosition.
 *
 * Key Features:
 * - Deep merges property changes into existing responsive position
 * - Preserves existing breakpoint styles that aren't being modified
 * - Supports targeting specific breakpoints
 * - Provides change summary logging for debugging
 * - Works with style, size, position, and constraints
 *
 * @see Documents/AI_Template_System/Roadmaps/AI_Chat_Enhancement_Roadmap_Phase2.md
 */

import { CSSProperties } from 'react';
import { ResponsivePosition, BreakpointOverride, Position, ResponsiveConstraints, ResponsiveSize } from '@/types';
import { deepMergeObjects } from '@/utils/deepMergeBreakpoints';
import { logger } from '@/utils/logger';

/**
 * Type for breakpoint names
 */
export type BreakpointName = 'mobile' | 'tablet' | 'desktop';

/**
 * Property changes structure that can be merged into responsive position
 */
export interface PropertyChange {
  style?: CSSProperties & { width?: string | number; height?: string | number };
  size?: { width?: string | number; height?: string | number };
  position?: Partial<Position>;
  constraints?: Partial<ResponsiveConstraints>;
  properties?: Record<string, unknown>;
  [key: string]: unknown;
}

/**
 * Result of a merge operation
 */
export interface MergeResult {
  /** The merged responsive position */
  mergedPosition: ResponsivePosition;
  /** List of breakpoints that were modified */
  affectedBreakpoints: BreakpointName[];
  /** Whether any actual changes were made */
  hasChanges: boolean;
}

/**
 * Change summary for a single property
 */
export interface PropertyChangeSummary {
  before: unknown;
  after: unknown;
}

/**
 * Change summary for logging and debugging
 */
export type ChangeSummary = Record<string, PropertyChangeSummary>;

/**
 * Default responsive position structure
 * Note: Flex-only architecture as of December 2025
 */
const DEFAULT_RESPONSIVE_POSITION: ResponsivePosition = {
  strategy: 'flex', // Changed from 'absolute' - flex-only architecture
  constraints: undefined,
  responsiveSize: undefined,
  breakpoints: {},
};

/**
 * ResponsivePositionMerger - Singleton service for deep merging responsive positions
 */
class ResponsivePositionMerger {
  private static instance: ResponsivePositionMerger | null = null;

  private constructor() {
    logger.debug("ResponsivePositionMerger initialized");
  }

  /**
   * Get the singleton instance of ResponsivePositionMerger
   */
  static getInstance(): ResponsivePositionMerger {
    if (!this.instance) {
      this.instance = new ResponsivePositionMerger();
    }
    return this.instance;
  }

  /**
   * Reset the singleton instance (useful for testing)
   */
  static resetInstance(): void {
    this.instance = null;
  }

  /**
   * Deep merges property changes into existing responsive position.
   * Preserves existing breakpoint styles that aren't being modified.
   *
   * @param existing The existing responsive position (can be undefined)
   * @param changes The property changes to merge
   * @param targetBreakpoint Optional breakpoint to target (defaults to 'desktop')
   * @returns MergeResult with the merged position and metadata
   */
  mergeChanges(
    existing: ResponsivePosition | undefined,
    changes: PropertyChange,
    targetBreakpoint?: BreakpointName
  ): MergeResult {
    // Start with deep clone of existing or default
    const merged: ResponsivePosition = existing
      ? this.deepClone(existing)
      : this.deepClone(DEFAULT_RESPONSIVE_POSITION);

    // Initialize breakpoints if needed
    if (!merged.breakpoints) {
      merged.breakpoints = {};
    }

    // Determine which breakpoints to update
    const breakpointsToUpdate: BreakpointName[] = targetBreakpoint
      ? [targetBreakpoint]
      : ['desktop']; // Default to desktop only

    const affectedBreakpoints: BreakpointName[] = [];
    let hasChanges = false;

    for (const bp of breakpointsToUpdate) {
      // Ensure breakpoint object exists
      if (!merged.breakpoints![bp]) {
        merged.breakpoints![bp] = {};
      }

      const breakpointData = merged.breakpoints![bp]!;
      let breakpointModified = false;

      // Merge style changes
      if (changes.style && Object.keys(changes.style).length > 0) {
        breakpointData.style = this.mergeObjects(
          breakpointData.style || {},
          changes.style as Record<string, unknown>
        );
        breakpointModified = true;
      }

      // Merge size changes into style (dimensions are stored in style)
      if (changes.size && Object.keys(changes.size).length > 0) {
        if (!breakpointData.style) {
          breakpointData.style = {};
        }

        if (changes.size.width !== undefined) {
          breakpointData.style.width = changes.size.width;
          breakpointModified = true;
        }
        if (changes.size.height !== undefined) {
          breakpointData.style.height = changes.size.height;
          breakpointModified = true;
        }
      }

      // Merge position changes
      if (changes.position && Object.keys(changes.position).length > 0) {
        breakpointData.position = this.mergeObjects(
          breakpointData.position || {},
          changes.position
        ) as Partial<Position>;
        breakpointModified = true;
      }

      // Merge constraints if present
      if (changes.constraints && Object.keys(changes.constraints).length > 0) {
        breakpointData.constraints = this.mergeObjects(
          (breakpointData.constraints || {}) as Record<string, unknown>,
          changes.constraints as Record<string, unknown>
        ) as Partial<ResponsiveConstraints>;
        breakpointModified = true;
      }

      if (breakpointModified) {
        affectedBreakpoints.push(bp);
        hasChanges = true;
      }
    }

    // Also merge top-level responsive position properties if they exist
    if (changes.constraints && !targetBreakpoint) {
      // If no target breakpoint specified, also update top-level constraints
      merged.constraints = this.mergeObjects(
        (merged.constraints || {}) as Record<string, unknown>,
        changes.constraints as Record<string, unknown>
      ) as ResponsiveConstraints;
    }

    return {
      mergedPosition: merged,
      affectedBreakpoints,
      hasChanges,
    };
  }

  /**
   * Merges changes into multiple breakpoints simultaneously.
   * Useful for commands like "make wider on all breakpoints".
   *
   * @param existing The existing responsive position
   * @param changes The property changes to merge
   * @param targetBreakpoints Array of breakpoints to update
   * @returns MergeResult with the merged position
   */
  mergeIntoMultipleBreakpoints(
    existing: ResponsivePosition | undefined,
    changes: PropertyChange,
    targetBreakpoints: BreakpointName[]
  ): MergeResult {
    let merged = existing ? this.deepClone(existing) : this.deepClone(DEFAULT_RESPONSIVE_POSITION);
    const allAffectedBreakpoints: BreakpointName[] = [];
    let hasChanges = false;

    for (const bp of targetBreakpoints) {
      const result = this.mergeChanges(merged, changes, bp);
      merged = result.mergedPosition;
      if (result.hasChanges) {
        hasChanges = true;
        if (!allAffectedBreakpoints.includes(bp)) {
          allAffectedBreakpoints.push(bp);
        }
      }
    }

    console.log('[Phase 5.4] Multi-breakpoint merge result:', {
      targetBreakpoints,
      affectedBreakpoints: allAffectedBreakpoints,
      hasChanges,
    });

    return {
      mergedPosition: merged,
      affectedBreakpoints: allAffectedBreakpoints,
      hasChanges,
    };
  }

  /**
   * Extracts only the changed properties for logging.
   * Compares before and after states to identify what actually changed.
   *
   * @param before The responsive position before changes
   * @param after The responsive position after changes
   * @returns Record mapping property paths to before/after values
   */
  getChangeSummary(
    before: ResponsivePosition | undefined,
    after: ResponsivePosition
  ): ChangeSummary {
    const changes: ChangeSummary = {};
    const breakpoints: BreakpointName[] = ['mobile', 'tablet', 'desktop'];

    for (const bp of breakpoints) {
      const beforeBp = before?.breakpoints?.[bp];
      const afterBp = after?.breakpoints?.[bp];

      // Compare styles
      const beforeStyle = beforeBp?.style || {};
      const afterStyle = afterBp?.style || {};

      // Check all keys in after style
      for (const key of Object.keys(afterStyle)) {
        const beforeValue = beforeStyle[key];
        const afterValue = afterStyle[key];

        if (!this.deepEqual(beforeValue, afterValue)) {
          changes[`${bp}.style.${key}`] = {
            before: beforeValue,
            after: afterValue,
          };
        }
      }

      // Check for removed keys (in before but not in after)
      for (const key of Object.keys(beforeStyle)) {
        if (!(key in afterStyle)) {
          changes[`${bp}.style.${key}`] = {
            before: beforeStyle[key],
            after: undefined,
          };
        }
      }

      // Compare position
      const beforePosition = beforeBp?.position || {};
      const afterPosition = afterBp?.position || {};

      for (const key of Object.keys(afterPosition)) {
        const beforeValue = (beforePosition as Record<string, unknown>)[key];
        const afterValue = (afterPosition as Record<string, unknown>)[key];

        if (!this.deepEqual(beforeValue, afterValue)) {
          changes[`${bp}.position.${key}`] = {
            before: beforeValue,
            after: afterValue,
          };
        }
      }

      // Compare constraints
      const beforeConstraints = beforeBp?.constraints || {};
      const afterConstraints = afterBp?.constraints || {};

      for (const key of Object.keys(afterConstraints)) {
        const beforeValue = (beforeConstraints as Record<string, unknown>)[key];
        const afterValue = (afterConstraints as Record<string, unknown>)[key];

        if (!this.deepEqual(beforeValue, afterValue)) {
          changes[`${bp}.constraints.${key}`] = {
            before: beforeValue,
            after: afterValue,
          };
        }
      }

      // Compare hidden property
      if (beforeBp?.hidden !== afterBp?.hidden) {
        changes[`${bp}.hidden`] = {
          before: beforeBp?.hidden,
          after: afterBp?.hidden,
        };
      }
    }

    // Compare top-level properties
    if (!this.deepEqual(before?.strategy, after?.strategy)) {
      changes['strategy'] = {
        before: before?.strategy,
        after: after?.strategy,
      };
    }

    if (!this.deepEqual(before?.layoutMode, after?.layoutMode)) {
      changes['layoutMode'] = {
        before: before?.layoutMode,
        after: after?.layoutMode,
      };
    }

    // Compare top-level constraints
    if (!this.deepEqual(before?.constraints, after?.constraints)) {
      changes['constraints'] = {
        before: before?.constraints,
        after: after?.constraints,
      };
    }

    return changes;
  }

  /**
   * Formats a change summary for logging.
   * Creates a human-readable string of what changed.
   *
   * @param summary The change summary from getChangeSummary
   * @returns Formatted string for logging
   */
  formatChangeSummary(summary: ChangeSummary): string {
    const lines: string[] = [];

    for (const [path, change] of Object.entries(summary)) {
      const beforeStr = change.before === undefined ? 'undefined' : JSON.stringify(change.before);
      const afterStr = change.after === undefined ? 'undefined' : JSON.stringify(change.after);
      lines.push(`  ${path}: ${beforeStr} → ${afterStr}`);
    }

    return lines.length > 0 ? lines.join('\n') : '  (no changes)';
  }

  /**
   * Validates that a merge operation will not lose data.
   * Checks that no breakpoint data will be unintentionally overwritten.
   *
   * @param existing The existing responsive position
   * @param changes The changes to apply
   * @param targetBreakpoint The target breakpoint
   * @returns Object with isValid flag and any warnings
   */
  validateMerge(
    existing: ResponsivePosition | undefined,
    changes: PropertyChange,
    targetBreakpoint?: BreakpointName
  ): { isValid: boolean; warnings: string[] } {
    const warnings: string[] = [];

    // Check if we're accidentally overwriting a complete style object
    if (changes.style && existing?.breakpoints) {
      const target = targetBreakpoint || 'desktop';
      const existingStyle = existing.breakpoints[target]?.style;

      if (existingStyle && Object.keys(existingStyle).length > 5) {
        // Existing has many style properties
        const overlappingKeys = Object.keys(changes.style).filter(
          key => key in existingStyle
        );
        if (overlappingKeys.length > 0) {
          warnings.push(
            `Overwriting existing style properties in ${target}: ${overlappingKeys.join(', ')}`
          );
        }
      }
    }

    // Check if we're modifying dimensions without affecting other breakpoints
    if (changes.size && existing?.breakpoints && !targetBreakpoint) {
      const otherBreakpoints: BreakpointName[] = ['mobile', 'tablet'];
      const desktopHasSize = !!(
        existing.breakpoints.desktop?.style?.width ||
        existing.breakpoints.desktop?.style?.height
      );

      for (const bp of otherBreakpoints) {
        const bpHasSize = !!(
          existing.breakpoints[bp]?.style?.width ||
          existing.breakpoints[bp]?.style?.height
        );
        if (bpHasSize && !desktopHasSize) {
          warnings.push(
            `${bp} has explicit dimensions but desktop doesn't. Consider updating ${bp} as well.`
          );
        }
      }
    }

    return {
      isValid: true, // Merge is always valid, warnings are informational
      warnings,
    };
  }

  private deepClone<T>(obj: T): T {
    return structuredClone(obj);
  }

  /**
   * Deep merges two objects, with source values overwriting target values.
   * Arrays are replaced, not merged.
   * Delegates to shared utility in @/utils/deepMergeBreakpoints.
   */
  private mergeObjects(
    target: Record<string, unknown>,
    source: Record<string, unknown>
  ): Record<string, unknown> {
    return deepMergeObjects(target, source);
  }

  /**
   * Deep equality check for two values.
   */
  private deepEqual(a: unknown, b: unknown): boolean {
    if (a === b) return true;
    if (a === null || b === null) return a === b;
    if (typeof a !== typeof b) return false;
    if (typeof a !== 'object') return a === b;

    if (Array.isArray(a) !== Array.isArray(b)) return false;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;
      return a.every((item, index) => this.deepEqual(item, b[index]));
    }

    const aObj = a as Record<string, unknown>;
    const bObj = b as Record<string, unknown>;

    const aKeys = Object.keys(aObj);
    const bKeys = Object.keys(bObj);

    if (aKeys.length !== bKeys.length) return false;

    return aKeys.every(key => this.deepEqual(aObj[key], bObj[key]));
  }

  /**
   * Helper to extract style from a breakpoint.
   * Returns empty object if breakpoint or style doesn't exist.
   */
  getBreakpointStyle(
    responsivePosition: ResponsivePosition | undefined,
    breakpoint: BreakpointName
  ): Record<string, unknown> {
    return (responsivePosition?.breakpoints?.[breakpoint]?.style as Record<string, unknown>) || {};
  }

  /**
   * Helper to extract a specific dimension from responsive position.
   * Checks breakpoint styles in priority order: specified > desktop > tablet > mobile.
   */
  getDimension(
    responsivePosition: ResponsivePosition | undefined,
    dimension: 'width' | 'height',
    preferredBreakpoint?: BreakpointName
  ): string | number | undefined {
    const breakpointOrder: BreakpointName[] = preferredBreakpoint
      ? [preferredBreakpoint, 'desktop', 'tablet', 'mobile']
      : ['desktop', 'tablet', 'mobile'];

    for (const bp of breakpointOrder) {
      const style = responsivePosition?.breakpoints?.[bp]?.style;
      if (style && style[dimension] !== undefined) {
        return style[dimension] as string | number;
      }
    }

    return undefined;
  }
}

// Export singleton instance
export const responsivePositionMerger = ResponsivePositionMerger.getInstance();

// Also export the class for testing
export { ResponsivePositionMerger };
